import axios from "axios";

import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";

export interface TournamentSourceMeta {
    mode: string;
    permissionKey: string;
    resource: string;
    readOnly: boolean;
    sourceStatus: string;
    notes: string[];
    manualSteps: string[];
}

export interface TournamentListResponse<T> {
    success: boolean;
    data: {
        items: T[];
        total: number;
        page: number;
        pageSize: number;
    };
    meta: TournamentSourceMeta;
}

export interface TournamentDetailResponse<T> {
    success: boolean;
    data: {
        item: T | null;
        found: boolean;
        requestedId: string;
    };
    meta: TournamentSourceMeta;
}

export interface TournamentBattleLogListItem {
    id: string;
    tournamentId: string | null;
    tournamentName: string | null;
    round: string | null;
    layoutId: string | null;
    environmentId: string | null;
    playerA: string | null;
    playerB: string | null;
    winnerId: string | null;
    status: string | null;
    durationSeconds: number | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface TournamentBattleParticipant {
    id: string;
    name: string;
    seed: number | null;
    rank: number | null;
    squad: string | null;
}

export interface TournamentBattleResult {
    stars: number;
    percent: number;
    scoreA: number;
    scoreB: number;
    replayStatus: string;
    summary: string;
    winnerId: string | null;
    startedAt: string;
    endedAt: string;
    durationSeconds: number;
    room: string;
    version: string;
    statVersion: string;
}

export interface TournamentReferenceLayout {
    id: string;
    name: string;
    difficulty: string;
    mapSize: string;
    category: string;
    status: string;
}

export interface TournamentReferenceEnvironment {
    id: string;
    name: string;
    type: string;
    version: string;
    status: string;
}

export interface TournamentBattleLogDetail {
    battle: TournamentBattleLogListItem | null;
    participants: TournamentBattleParticipant[];
    result: TournamentBattleResult | null;
    layout: TournamentReferenceLayout | null;
    environment: TournamentReferenceEnvironment | null;
    rawPayload: Record<string, unknown> | null;
}

export interface TournamentLayoutListItem {
    id: string;
    name: string | null;
    difficulty: string | null;
    environmentId: string | null;
    status: string | null;
    mapSize: string | null;
    category: string | null;
    usageCount: number;
    updatedAt: string | null;
}

export interface TournamentLinkedTournament {
    id: string;
    name: string;
    rounds: string[];
    usageCount: number;
}

export interface TournamentLayoutDetail extends TournamentLayoutListItem {
    metadata: Record<string, unknown> | null;
    placements: Array<Record<string, unknown>>;
    rules: Record<string, unknown> | null;
    linkedTournaments: TournamentLinkedTournament[];
    rawPayload: Record<string, unknown> | null;
}

export interface TournamentEnvironmentListItem {
    id: string;
    name: string | null;
    type: string | null;
    version: string | null;
    status: string | null;
    linkedLayoutsCount: number;
    battleUsageCount: number;
    updatedAt: string | null;
}

export interface TournamentEnvironmentDetail extends TournamentEnvironmentListItem {
    rules: Record<string, unknown> | null;
    modifiers: Array<Record<string, unknown>>;
    linkedLayouts: TournamentLayoutListItem[];
    recentBattles: TournamentBattleLogListItem[];
    rawPayload: Record<string, unknown> | null;
}

const buildHeaders = (activeEnv?: string) => ({
    Authorization: Helpers.getSessionId() || "",
    ...(activeEnv ? { env: activeEnv } : {}),
});

export const fetchTournamentList = <T>(path: string, params: Record<string, unknown>, activeEnv?: string): Promise<TournamentListResponse<T>> =>
    axios.get<TournamentListResponse<T>>(`${Config.getInstance.apiUrl}${path}`, {
        params,
        headers: buildHeaders(activeEnv),
    }).then(response => response.data as TournamentListResponse<T>);

export const fetchTournamentDetail = <T>(path: string, activeEnv?: string): Promise<TournamentDetailResponse<T>> =>
    axios.get<TournamentDetailResponse<T>>(`${Config.getInstance.apiUrl}${path}`, {
        headers: buildHeaders(activeEnv),
    }).then(response => response.data as TournamentDetailResponse<T>);
